import { ArrowLeft, ArrowRight, Download, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import type { Translations } from "@/lib/i18n/types";
import type { ProtocolStep } from "./steps";
import type { Language } from "@/lib/i18n/types";
import { ProtocolLanguagePicker } from "./ProtocolLanguagePicker";
import { ProtocolDesignPicker } from "./ProtocolDesignPicker";
import type { ProtocolLayout } from "@/domains/pm/utils/protocolLayout";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface ProtocolDialogActionsProps {
  step: ProtocolStep;
  canContinue: boolean;
  canExport: boolean;
  /** Why Export is unavailable, rendered rather than left implicit — a
   *  disabled button fires no pointer events, so a tooltip could not open. */
  blockedReason?: string;
  exporting: boolean;
  previewing: boolean;
  /** The attached files' bytes are being read, ahead of the render. */
  preparingAttachments: boolean;
  copy: ProtocolCopy;
  /** The document's own language. Offered only on the content step — before
   *  there is a document to apply it to it is a choice about nothing. */
  pdfLanguage: Language;
  onPdfLanguageChange: (language: Language) => void;
  /** The document's look, offered beside its language on the content step. */
  layout: ProtocolLayout;
  onLayoutChange: (layout: ProtocolLayout) => void;
  onBack: () => void;
  onContinue: () => void;
  onPreview: () => void;
  onExport: () => void;
}

export function ProtocolDialogActions(props: ProtocolDialogActionsProps) {
  const busy = props.exporting || props.previewing;
  const reason = props.step === "content" && !busy ? props.blockedReason : undefined;
  return (
    // No Cancel button: the dialog's own close (×, Escape) aborts every read
    // and render and closes at once, on every step and mid-render — a stalled
    // socket can never leave the user behind a modal whose controls are dead.
    <DialogFooter className="flex-row flex-wrap items-center gap-2 sm:justify-between">
      {props.step !== "dates" ? (
        <Button
          variant="outline"
          size="icon"
          disabled={busy}
          onClick={props.onBack}
          aria-label={props.copy.back}
          title={props.copy.back}
        >
          <ArrowLeft aria-hidden className="h-4 w-4" />
        </Button>
      ) : (
        // Holds the left edge so the controls stay on the right on step 1.
        <span aria-hidden />
      )}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        {props.step === "content" && (
          <>
            <ProtocolDesignPicker
              value={props.layout}
              copy={props.copy}
              onChange={props.onLayoutChange}
            />
            <ProtocolLanguagePicker
              value={props.pdfLanguage}
              copy={props.copy}
              onChange={props.onPdfLanguageChange}
            />
          </>
        )}
        {props.step !== "content" ? (
          <Button
            size="icon"
            disabled={!props.canContinue}
            onClick={props.onContinue}
            aria-label={props.copy.continue}
            title={props.copy.continue}
          >
            <ArrowRight aria-hidden className="h-4 w-4" />
          </Button>
        ) : (
          <ContentStepActions {...props} busy={busy} />
        )}
      </div>
      {reason && <p className="w-full text-right text-xs text-muted-foreground">{reason}</p>}
    </DialogFooter>
  );
}

/** Preview and Export, which only the last step offers. */
function ContentStepActions(props: ProtocolDialogActionsProps & { busy: boolean }) {
  const disabled = !props.canExport || props.busy;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="gap-1.5"
        disabled={disabled}
        onClick={props.onPreview}
      >
        {props.previewing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileText aria-hidden className="h-4 w-4" />
        )}
        {props.previewing
          ? props.preparingAttachments
            ? props.copy.attachments.preparing
            : props.copy.previewing
          : props.copy.preview}
      </Button>
      <Button className="gap-1.5" disabled={disabled} onClick={props.onExport}>
        {props.exporting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {props.preparingAttachments ? props.copy.attachments.preparing : props.copy.exporting}
          </>
        ) : (
          <>
            <Download aria-hidden className="h-4 w-4" />
            {props.copy.export}
          </>
        )}
      </Button>
    </>
  );
}
